import React, { useState, useEffect } from "react";
import { message, Spin } from "antd";
import {
  collection, query, orderBy, getDocs, addDoc,
  serverTimestamp, where, updateDoc, doc, getDoc
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const EMOJIS = { love: "💖", like: "👍🏻", meh: "😑", bad: "💩" };
const STATUS_STYLE = {
  Proposé:  { bg: "#eff6ff", color: "#1d4ed8" },
  Accepté:  { bg: "#f0fdf4", color: "#15803d" },
  Refusé:   { bg: "#fef2f2", color: "#dc2626" },
};

export default function Feedback() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [votedIds, setVotedIds] = useState(new Set());
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");

  const toggleTheme = () => {
    setDark(d => {
      localStorage.setItem("ws-theme", !d ? "dark" : "light");
      return !d;
    });
  };

  const t = dark ? theme.dark : theme.light;

  const fetchIdeas = async () => {
    try {
      const snap = await getDocs(query(collection(db, "ideas"), orderBy("created_at", "desc")));
      setIdeas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch {
      message.error("Erreur de chargement.");
    }
    setLoading(false);
  };

  const fetchVotes = async () => {
    if (!user?.uid) return;
    try {
      const snap = await getDocs(query(collection(db, "votes"), where("user_id", "==", user.uid)));
      setVotedIds(new Set(snap.docs.map(d => d.data().idea_id)));
    } catch {}
  };

  useEffect(() => {
    if (user) { fetchIdeas(); fetchVotes(); }
  }, [user]);

  const submitIdea = async () => {
    if (!title.trim()) return message.warning("Le titre est requis.");
    setSubmitting(true);
    try {
      await addDoc(collection(db, "ideas"), {
        title,
        description,
        created_by: user.uid,
        status: "Proposé",
        votes_love: 0,
        votes_like: 0,
        votes_meh: 0,
        votes_bad: 0,
        created_at: serverTimestamp(),
      });
      setTitle("");
      setDescription("");
      message.success("Idée soumise ! 🎉");
      fetchIdeas();
    } catch {
      message.error("Erreur lors de la soumission.");
    }
    setSubmitting(false);
  };

  const voteIdea = async (idea, reaction) => {
    if (!user?.uid) return;
    if (votedIds.has(idea.id)) return message.warning("Tu as déjà voté pour cette idée.");

    try {
      await addDoc(collection(db, "votes"), {
        user_id: user.uid,
        idea_id: idea.id,
        reaction,
        created_at: serverTimestamp(),
      });

      const ideaRef = doc(db, "ideas", idea.id);
      const field = `votes_${reaction}`;
      await updateDoc(ideaRef, { [field]: (idea[field] || 0) + 1 });

      setVotedIds(prev => new Set([...prev, idea.id]));
      setIdeas(prev => prev.map(i => i.id === idea.id ? { ...i, [field]: (i[field] || 0) + 1 } : i));
    } catch {
      message.error("Erreur lors du vote.");
    }
  };

  if (!user) return null;

  return (
    <div style={{ background: t.bg, minHeight: "100vh" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", paddingBottom: 40 }}>

        {/* Topbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px 0", flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo.PNG" alt="WeStudy" style={{ height: 40, objectFit: "contain" }} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: t.text1 }}>Vote & Suggestions</div>
              <div style={{ fontSize: 11, color: t.text2 }}>Propose des idées pour améliorer le centre</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={toggleTheme} style={{ ...s.toggleTrack, background: t.bg3, border: `1px solid ${t.border}` }}>
              <div style={{ ...s.toggleKnob, background: t.blue, transform: dark ? "translateX(16px)" : "translateX(0)" }} />
            </button>
            <button onClick={() => navigate("/")} style={{ ...s.outlineBtn, background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>
              ← Retour
            </button>
          </div>
        </div>

        {/* Submit form */}
        <div style={{ padding: "20px 18px 0" }}>
          <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: t.text3, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>
              💡 Proposer une idée
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Titre de l'idée"
                style={{ ...s.input, background: t.bg3, border: `1px solid ${t.border}`, color: t.text1 }}
              />
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Description (optionnelle)"
                rows={3}
                style={{ ...s.input, background: t.bg3, border: `1px solid ${t.border}`, color: t.text1, resize: "vertical", padding: "10px 12px", height: "auto" }}
              />
              <button
                onClick={submitIdea}
                disabled={submitting}
                style={{ ...s.primaryBtn, background: submitting ? t.border : t.blue, color: submitting ? t.text3 : "#fff", alignSelf: "flex-start", padding: "9px 20px" }}
              >
                {submitting ? "Envoi..." : "✉️ Soumettre"}
              </button>
            </div>
          </div>
        </div>

        {/* Ideas list */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "24px 18px 10px" }}>
          <span style={{ fontSize: 11, fontWeight: 500, color: t.text3, letterSpacing: "0.08em", textTransform: "uppercase" }}>🗳️ Les idées</span>
          <span style={{ fontSize: 11, background: t.bg3, color: t.text2, borderRadius: 20, padding: "1px 8px" }}>{ideas.length}</span>
          <div style={{ flex: 1, height: 1, background: t.border }} />
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin size="large" />
          </div>
        ) : ideas.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 24px" }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>💡</div>
            <div style={{ fontSize: 14, color: t.text1, marginBottom: 4 }}>Aucune idée pour l'instant</div>
            <div style={{ fontSize: 12, color: t.text2 }}>Soyez le premier à proposer une amélioration !</div>
          </div>
        ) : (
          <ResponsiveGrid>
            {ideas.map(idea => (
              <IdeaCard
                key={idea.id}
                idea={idea}
                onVote={voteIdea}
                hasVoted={votedIds.has(idea.id)}
                t={t}
              />
            ))}
          </ResponsiveGrid>
        )}
      </div>
    </div>
  );
}

function IdeaCard({ idea, onVote, hasVoted, t }) {
  const status = idea.status || "Proposé";
  const statusStyle = STATUS_STYLE[status] || { bg: t.bg3, color: t.text2 };
  const totalVotes = Object.keys(EMOJIS).reduce((sum, k) => sum + (idea[`votes_${k}`] || 0), 0);

  return (
    <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: t.text1, lineHeight: 1.4, flex: 1 }}>{idea.title}</div>
        <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 20, background: statusStyle.bg, color: statusStyle.color, whiteSpace: "nowrap", flexShrink: 0 }}>
          {status}
        </span>
      </div>

      {/* Description */}
      {idea.description && (
        <div style={{ fontSize: 12, color: t.text2, lineHeight: 1.5 }}>{idea.description}</div>
      )}

      {/* Divider */}
      <div style={{ height: 1, background: t.border }} />

      {/* Vote buttons */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {Object.entries(EMOJIS).map(([key, emoji]) => {
          const count = idea[`votes_${key}`] || 0;
          return (
            <button
              key={key}
              onClick={() => !hasVoted && onVote(idea, key)}
              style={{
                display: "flex", alignItems: "center", gap: 5,
                padding: "6px 10px", borderRadius: 20,
                background: hasVoted ? t.bg3 : t.bg3,
                border: `1px solid ${t.border}`,
                color: t.text1, fontSize: 14,
                cursor: hasVoted ? "not-allowed" : "pointer",
                opacity: hasVoted ? 0.6 : 1,
                transition: "opacity 0.15s",
              }}
            >
              <span style={{ fontSize: 16 }}>{emoji}</span>
              <span style={{ fontSize: 12, color: t.text2 }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Total + voted badge */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 11, color: t.text3 }}>{totalVotes} vote{totalVotes !== 1 ? "s" : ""}</span>
        {hasVoted && (
          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: t.greenBg, color: t.green }}>
            ✓ Voté
          </span>
        )}
      </div>
    </div>
  );
}

function ResponsiveGrid({ children }) {
  const [cols, setCols] = useState(1);
  useEffect(() => {
    const update = () => setCols(window.innerWidth >= 640 ? 2 : 1);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, gap: 10, padding: "0 18px" }}>
      {children}
    </div>
  );
}

const theme = {
  light: {
    bg: "#f8fafc", bg3: "#f1f5f9", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb",
    green: "#16a34a", greenBg: "#f0fdf4",
    cardBg: "#ffffff",
  },
  dark: {
    bg: "#0f172a", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD",
    green: "#4ade80", greenBg: "#052e16",
    cardBg: "#1e293b",
  },
};

const s = {
  toggleTrack: { width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", flexShrink: 0 },
  toggleKnob: { width: 14, height: 14, borderRadius: "50%", position: "absolute", top: 2, left: 2, transition: "transform 0.2s" },
  outlineBtn: { fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer" },
  primaryBtn: { border: "none", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "opacity 0.15s" },
  input: { width: "100%", height: 40, borderRadius: 10, padding: "0 12px", fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit" },
};